import { Alert, Box, Button, CircularProgress, Link, Stack, TextField, Typography } from '@mui/material';
import axios from 'axios';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { PilotRequestApi } from '../api/PilotRequestApi';
import VerbaScholaAccountCard, { accountBodySx, accountLinkSx } from '../component/VerbaScholaAccountCard';
import { primaryButtonSx } from '../style/verbaScholaPublicStyles';

export default function PilotRequest() {
    const [request, setRequest] = React.useState({ name: '', email: '', organization: '', language: 'Mandarin Chinese', message: '', website: '' });
    const [sending, setSending] = React.useState(false);
    const [sent, setSent] = React.useState(false);
    const [error, setError] = React.useState('');
    const errorRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);

    const change = (field: keyof typeof request) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setRequest(current => ({ ...current, [field]: event.target.value }));
    };
    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (sending) return;
        setSending(true);
        setError('');
        PilotRequestApi.send(request)
            .then(() => setSent(true))
            .catch(err => {
                const status = axios.isAxiosError(err) ? err.response?.status : undefined;
                setError(status === 429
                    ? 'Too many requests from this connection. Please try again later or email support@verbaschola.com.'
                    : status === 400
                        ? 'Check the name, school email, school and language fields. Each needs at least 2 characters.'
                        : 'We could not send your request right now. Please try again or email support@verbaschola.com.');
            })
            .finally(() => setSending(false));
    };

    if (sent) return <VerbaScholaAccountCard title="Request received" intro="Thanks for your interest in VerbaSchola. We'll follow up by email.">
        <Button component={RouterLink} to="/" sx={{ ...primaryButtonSx, minHeight: 48, mt: 3, width: '100%' }}>Back to home</Button>
    </VerbaScholaAccountCard>;

    return <VerbaScholaAccountCard title="Request a pilot" width={500}
        intro="Tell us about your class. We'll follow up with a short demo and teacher account setup.">
        {error && <Alert severity="error" id="pilot-error" ref={errorRef} tabIndex={-1} sx={{ mt: 3 }}>{error}</Alert>}
        <Stack component="form" spacing={2} onSubmit={submit} sx={{ mt: 3 }}>
            <TextField label="Your name" name="name" autoComplete="name" required fullWidth inputProps={{ minLength: 2, maxLength: 120 }}
                value={request.name} onChange={change('name')} />
            <TextField label="School email" name="email" type="email" autoComplete="email" required fullWidth inputProps={{ maxLength: 254 }}
                value={request.email} onChange={change('email')} />
            <TextField label="School or institution" name="organization" autoComplete="organization" required fullWidth inputProps={{ minLength: 2, maxLength: 160 }}
                value={request.organization} onChange={change('organization')} />
            <TextField label="Language you teach" name="language" required fullWidth inputProps={{ minLength: 2, maxLength: 80 }}
                value={request.language} onChange={change('language')} />
            <TextField label="Anything we should know? (optional)" name="message" multiline minRows={3} fullWidth inputProps={{ maxLength: 2000 }}
                helperText="For example: grade level, class size, or when you'd like to start."
                value={request.message} onChange={change('message')} />
            {/* Spam trap: visually hidden (1px, not MUI's 1 = 100%), skipped by keyboard and screen readers. */}
            <Box sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }} aria-hidden>
                <input tabIndex={-1} name="website" autoComplete="off" value={request.website} onChange={change('website')} />
            </Box>
            <Button type="submit" disabled={sending} sx={{ ...primaryButtonSx, minHeight: 48 }}>
                {sending ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} sx={{ color: 'inherit' }} aria-hidden /><span>Sending…</span></Stack> : 'Send request'}
            </Button>
        </Stack>
        <Typography sx={{ ...accountBodySx, mt: 3, textAlign: 'center' }}>
            Prefer email? <Link href="mailto:support@verbaschola.com" sx={accountLinkSx}>support@verbaschola.com</Link>
        </Typography>
    </VerbaScholaAccountCard>;
}
